import { useTranslation } from 'react-i18next';
import { ApiErrorText, Loader, ResultContent } from '@/shared/components';
import type { UseFormReturn } from 'react-hook-form';
import type { DepositSelectOption } from '@/shared/types';
import {
  SccrsForm,
  type SccrsFormValues,
  type SccrsGamingHall,
} from '@/entities/sccrs';

interface Props {
  form: UseFormReturn<SccrsFormValues>;
  handleFormSubmit: (data: SccrsFormValues) => void;
  currencyOptions: DepositSelectOption[];
  platformOptions: SccrsGamingHall[];
  commissionData:
    | {
        feeAmount: number;
        currencyCode: string;
        receiveAmount: number;
      }
    | undefined;
  amountPlaceholder: string;
  shouldShowCommission?: boolean;
  isSubmitInProgressWithoutErrors: boolean;
  formError?: boolean;
  isLoading: boolean;
  firstError: string | undefined;
  showResultModal: boolean;
  isSuccess: boolean;
  onBack?: () => void;
}

export const DepositSccrsForm = ({
  form,
  handleFormSubmit,
  currencyOptions,
  platformOptions,
  commissionData,
  amountPlaceholder,
  shouldShowCommission,
  formError,
  isLoading,
  firstError,
  showResultModal,
  isSuccess,
  onBack,
}: Props) => {
  const { t } = useTranslation();

  if (showResultModal) {
    const resultTitle = t(
      isSuccess
        ? 'depositMethods.sccrs.result.successTitle'
        : 'depositMethods.sccrs.result.errorTitle'
    );
    const resultDescription = t(
      isSuccess
        ? 'depositMethods.sccrs.result.successDescription'
        : 'depositMethods.sccrs.result.errorDescription'
    );
    const resultButtonText = t(
      isSuccess
        ? 'depositMethods.sccrs.result.successButton'
        : 'depositMethods.sccrs.result.errorButton'
    );

    return (
      <ResultContent
        className="w-full"
        isSuccess={isSuccess}
        title={resultTitle}
        description={resultDescription}
        buttonText={resultButtonText}
        onClick={onBack}
      />
    );
  }

  if (!form) {
    return <div>Form is not available</div>;
  }

  if (firstError) {
    return <ApiErrorText error={firstError} />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[4rem]">
        <Loader />
      </div>
    );
  }

  return (
    <SccrsForm
      form={form}
      handleFormSubmit={handleFormSubmit}
      currencyOptions={currencyOptions}
      platformOptions={platformOptions}
      amountPlaceholder={amountPlaceholder}
      commissionData={commissionData}
      shouldShowCommission={shouldShowCommission}
      formError={formError}
    />
  );
};
